'use client'

import { trackEvent } from '@acko-tech/building-blocks.ui.acko-analytics'
import { type ReactNode, useRef } from 'react'
import { Accordion } from '@acko/accordion'
import parse from 'html-react-parser'
import { ArticleH2 } from '../ArticleHeading'
import { htmlWithoutInlineStyleParseOptions } from '../htmlParseOptions'

export type FAQItem = {
  id?: string
  question: string
  answer: ReactNode | string
}

type FAQAccordionComponentFields = {
  headerTabName?: unknown
  title?: unknown
  items?: unknown
}

export type FAQAccordionProps = {
  component?: FAQAccordionComponentFields
  items?: FAQItem[]
  title?: string
}

const asString = (value: unknown) => (typeof value === 'string' ? value : undefined)

const asRecord = (value: unknown) =>
  value && typeof value === 'object' && !Array.isArray(value) ? (value as Record<string, unknown>) : undefined

const getFAQItems = (value: unknown): FAQItem[] =>
  (Array.isArray(value) ? value : [])
    .map<FAQItem | undefined>(item => {
      const record = asRecord(item)
      const question = asString(record?.question)
      const answer = asString(record?.answer)

      return question && answer
        ? {
            id: asString(record?.id),
            question,
            answer,
          }
        : undefined
    })
    .filter((item): item is FAQItem => Boolean(item))

const renderAnswer = (answer: FAQItem['answer']) =>
  typeof answer === 'string' ? (
    <div className="[font-size:var(--font-body-md-size)] [line-height:var(--font-body-md-line)] [letter-spacing:var(--font-body-md-spacing)] [font-weight:var(--font-body-md-weight)] [color:var(--color-text-secondary)] [&_a]:[color:var(--color-text-brand)]! [&_a:hover]:underline">
      {parse(answer, htmlWithoutInlineStyleParseOptions)}
    </div>
  ) : (
    <div className="[font-size:var(--font-body-md-size)] [line-height:var(--font-body-md-line)] [letter-spacing:var(--font-body-md-spacing)] [font-weight:var(--font-body-md-weight)] [color:var(--color-text-secondary)]">
      {answer}
    </div>
  )

export const FAQAccordion = ({ component, items, title }: FAQAccordionProps) => {
  const resolvedItems = getFAQItems(component?.items ?? items)
  const resolvedTitle = asString(component?.title) ?? title
  const accordionItems = resolvedItems.map((item, idx) => ({
    value: item.id ?? `faq-${idx}`,
    trigger: (
      <h3 className="[font-size:var(--font-heading-sm-size)] [line-height:var(--font-heading-sm-line)] lg:[font-size:var(--font-heading-md-size)] lg:[line-height:var(--font-heading-md-line)] [font-weight:var(--font-heading-sm-weight)]">
        {item.question}
      </h3>
    ),
    content: renderAnswer(item.answer),
  }))
  const defaultOpenValues = accordionItems[0]?.value ? [accordionItems[0].value] : []
  const openValuesRef = useRef(defaultOpenValues)

  if (!resolvedItems.length) return null

  const handleAccordionChange = (openValues: string[]) => {
    const openedValue = openValues.find(value => !openValuesRef.current.includes(value))
    openValuesRef.current = openValues

    if (!openedValue) return

    const openedItem = resolvedItems[accordionItems.findIndex(item => item.value === openedValue)]

    void trackEvent('tap_btn_engage', {
      component_name: 'FAQ',
      cta_text: openedItem?.question ?? '',
    })
  }

  return (
    <div className="flex flex-col gap-[16px]">
      {resolvedTitle && <ArticleH2>{resolvedTitle}</ArticleH2>}
      <Accordion
        className="[&_.acko-accordion-trigger]:px-0! [&_.acko-accordion-content-body]:px-0!"
        type="multiple"
        items={accordionItems}
        defaultValue={defaultOpenValues.length ? defaultOpenValues : undefined}
        onChange={handleAccordionChange}
        collapsible
      />
    </div>
  )
}
